/* eslint-disable eqeqeq */
/* eslint-disable no-undef */
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import router from 'next/router';
import produce from 'immer';
import { IChatStore } from 'stores/chat/types';
import { showChatNotification } from 'global/Notification';
import useScrollChat from 'features/chat/MainContent/useScrollChat';
import { roomDetailStore, groupMemberStore } from 'features/chat/store';
import { selectors as profileSelectors } from 'lib/entities/profile';
import webSocketSlice, {
  chatWsSocialV2Selector,
} from 'global/WebSocketV2/slice';
import { selectors } from 'lib/entities/chat/slice';

import { selectors as notificationSelector } from 'features/edit-profile/slice';
import {
  ATTACHMENT_TYPE,
  EVENT_TYPE_BROADCAST,
  MESSAGE_BODY_TYPE,
  RoomTypeFromPayload,
  StateChatList,
  WS_SOCIAL_VERSION,
  NOTIF_GROUP_CHAT,
  NOTIF_SETTING_KEY_DN,
} from 'features/chat/constant';
import useChatStore from 'stores/chat';
import { getNotifType, snakelizeKeys } from 'features/chat/utils';
import { transformToNotifMessage } from 'utils/socketSocial';
import { ChatWSSocialV2Payload } from '../../@types/chat';
import {
  NotificationSettingKeyPlatform,
  NotificationSettings,
  NotificationSettingsList,
} from '../../@types/notifications';

const checkMessageDuplicated = (messages, newRoomId, lastMessageId) => {
  if (messages[newRoomId]) {
    return messages[newRoomId]?.messages.some(
      (chat) => chat.id === lastMessageId,
    );
  }
  return false;
};

const checkMissingObjectText = (body) => {
  const chatMessageV1 =
    body?.event?.data?.chatMessage?.[WS_SOCIAL_VERSION]?.message;
  const valueText = chatMessageV1?.body?.conversation?.text
    ? chatMessageV1?.body?.conversation?.text
    : '';
  return {
    ...chatMessageV1,
    text: valueText,
    body: {
      ...chatMessageV1.body,
      conversation: {
        ...chatMessageV1?.body?.conversation,
        text: valueText,
        attachmentType:
          chatMessageV1?.body?.conversation?.attachmentType ||
          ATTACHMENT_TYPE.ATTACHMENT_TYPE_UNSPECIFIED,
      },
    },
  };
};

const handleRefreshGroupRoom = async (chatId) => {
  const hasRoomDetail = await roomDetailStore
    .getState()
    .refreshGroupRoomDetail(chatId);
  if (hasRoomDetail) {
    const groupId = roomDetailStore.getState().group[chatId]?.id;
    await groupMemberStore.getState().fetchGroupMember(groupId, 0);
    useChatStore.setState(
      produce((state: IChatStore) => {
        state.conversation.isLoading = false;
      }),
    );
  }
};

function useChatListenerWsSocialV2(isUserLogin = false) {
  const dispatch = useDispatch();
  const { activeRoomId } = useSelector(selectors);
  const {
    notifications: { data: notif },
  } = useSelector(notificationSelector);

  const { data: dataLoggedInUser } = useSelector(profileSelectors);
  const newIncomingChatWsSocialV2 = useSelector(chatWsSocialV2Selector);

  const messages = useChatStore((store) => store.conversation.messages);
  const defaultRooms = useChatStore((store) => store.rooms.default.list);
  const invitedRooms = useChatStore((store) => store.rooms.invited.list);
  const updateRoomListFromWS = useChatStore(
    (store) => store.updateRoomListFromWS,
  );
  const pushMessage = useChatStore((store) => store.pushMessage);
  const updateMessageFromWS = useChatStore(
    (store) => store.updateMessageFromWS,
  );

  const { scrollToBottom } = useScrollChat();

  const actionChatRoom = (incomingChat: ChatWSSocialV2Payload) => {
    if (activeRoomId > 0) {
      const { body } = incomingChat;
      let newMessage = checkMissingObjectText(body);
      let newRoom = body?.event?.data?.chatMessage?.[WS_SOCIAL_VERSION]?.room;

      const referenceId =
        body?.event?.data?.chatMessage?.[WS_SOCIAL_VERSION]?.referenceId;

      const roomStatus =
        body?.event?.data?.chatMessage?.[WS_SOCIAL_VERSION]?.roomStatus;
      newMessage = snakelizeKeys(newMessage);
      newRoom = snakelizeKeys(newRoom);
      const isDuplicated = messages
        ? checkMessageDuplicated(messages, newRoom?.id, newMessage?.id)
        : false;
      // Check if the event comes when user leave
      const isStateUnspecified =
        newRoom?.state === StateChatList.ROOM_STATE_UNSPECIFIED;
      const isDeletedEvent = newMessage?.body?.conversation?.flag?.is_deleted
        ? newMessage?.body?.conversation?.flag?.is_deleted
        : false;

      if (!isDuplicated && !isStateUnspecified && !isDeletedEvent) {
        const chatId = newRoom.id;
        // push message to state;
        pushMessage({
          chatId,
          message: newMessage,
          reference_id: referenceId,
          room_status: roomStatus,
          room: newRoom,
        });

        // scroll to bottom if in current active chat is receive new message
        const isChatPageShowed =
          router.asPath.includes(`/chat/${chatId}`) ||
          router.asPath.includes(`/group-chat/${chatId}`) ||
          router.asPath.includes(`/broadcast-chat/${chatId}`);

        const senderUserData = newMessage?.body?.conversation?.sender?.user;

        const senderUserName =
          senderUserData && senderUserData.username
            ? senderUserData.username
            : '';

        const isReceiver = senderUserName !== dataLoggedInUser.username;

        if (isChatPageShowed) {
          const container = document.getElementById('chat-content-container');
          const currentPos =
            container.scrollHeight - container.clientHeight - 200;
          const currentlyScrollingUp = container.scrollTop < currentPos;
          // when there is incoming message on receiver side, that is currently scrolling up
          // don't scroll to bottom
          if ((isReceiver && !currentlyScrollingUp) || !isReceiver) {
            scrollToBottom();
          }
        }
        // Handle event type message from ws in group chat
        // will refresh the room detail and member list
        const isCurrentPage = router.asPath.includes(`/group-chat/${chatId}`);
        const isEventMessage =
          newMessage?.body_type === MESSAGE_BODY_TYPE.MESSAGE_BODY_TYPE_EVENT;
        const isNotLeaveEvent =
          (newMessage?.body?.event?.text || '').toLowerCase() !== 'you left';

        if (isCurrentPage && isEventMessage && isNotLeaveEvent) {
          handleRefreshGroupRoom(chatId);
        }
      } else {
        // update message if id has available on convo,
        updateMessageFromWS({
          chatId: newRoom.id,
          messageId: newMessage.id,
          message: newMessage,
        });
      }
    }
  };

  const actionRoomList = (incomingChat: ChatWSSocialV2Payload) => {
    const { body } = incomingChat;

    let newRoom = body?.event?.data?.chatRoomInfo?.[WS_SOCIAL_VERSION]?.room;
    newRoom = snakelizeKeys(newRoom);

    const messageId = newRoom?.last_message?.id;

    const isAccepted = newRoom?.state === StateChatList.ROOM_STATE_ACCEPTED;

    const isPersonalChat = newRoom?.type === RoomTypeFromPayload.PERSONAL;
    const isBroadcast =
      // TODO: remove 3
      // While waiting for proto definition encoding is done,
      // we use number 3 as broadcast type,
      // Currently, BE is sending 3 instead of ROOM_TYPE_BROADCAST

      newRoom?.type === RoomTypeFromPayload.BROADCAST;

    const roomList = isAccepted ? defaultRooms : invitedRooms;

    let lastMessage: any = produce(newRoom?.last_message, (draft) => {
      draft.text = newRoom?.last_message?.text ? newRoom.last_message.text : '';
      draft.sender_username = newRoom.last_message?.sender_username || '';

      // Replace last message if user delete their own latest chat
      if (
        newRoom?.last_message?.text === 'This message was deleted' &&
        newRoom.last_message?.sender_username === dataLoggedInUser.username
      ) {
        draft.text = 'You deleted this message';
        draft.placeholder_mask.masked_text = 'You deleted this message';
      }
    });

    // ws social not send sender userId
    const senderUserName = lastMessage.sender_username;

    const isReceiver = senderUserName !== dataLoggedInUser.username;

    const isEventMessage =
      isPersonalChat || isBroadcast ? false : !senderUserName;

    const roomIdx = roomList.findIndex((item) => item.id === newRoom?.id);

    const isDuplicated = messages
      ? checkMessageDuplicated(messages, newRoom?.id, newRoom?.last_message?.id)
      : false;

    // existing chatroom
    if (roomIdx > -1) {
      const roomInfo =
        isPersonalChat || isBroadcast
          ? roomList[roomIdx].info
          : {
              avatar_url: newRoom?.info?.avatar_url || '',
              ...newRoom?.info,
            };

      const updateLastMessage = isDuplicated
        ? messages[newRoom?.id]?.messages[0]?.id === messageId
        : true;

      lastMessage = updateLastMessage
        ? lastMessage
        : roomList[roomIdx].last_message;

      let unreadCount = roomList[roomIdx].unread_count || 0;

      // counter unread count chat list
      if (!isEventMessage && isReceiver && !isDuplicated) {
        unreadCount = roomList[roomIdx].unread_count + 1;
      }

      newRoom = {
        ...roomList[roomIdx],
        last_message: lastMessage,
        info: roomInfo,
        unread_count: unreadCount,
        status: newRoom?.status,
        isBroadcast,
      };
    } else {
      let unreadCount = 0;
      // counter unread count chat list
      if (!isEventMessage && isReceiver && !isDuplicated) {
        unreadCount += 1;
      }
      newRoom = {
        ...newRoom,
        unread_count: unreadCount,
        isBroadcast,
      };
    }

    // wont update the room list if the event is us leaving
    if (newRoom?.lastMessage?.text !== 'You left') {
      updateRoomListFromWS(newRoom);
    }

    // Show Left Bottom Push Notification
    const isInvitedChat = newRoom.state === StateChatList.ROOM_STATE_INVITED;
    const isMuted = newRoom.status.isMuted || false;

    const getCurrentIncomingNotifType = getNotifType(newRoom)?.notifType;

    const incomingMessageDataNotifSetting = notif
      .find(
        (notifGroup: NotificationSettingsList) =>
          notifGroup.group === NOTIF_GROUP_CHAT,
      )
      .notifications.find(
        (notifItem: NotificationSettings) =>
          notifItem.type === getCurrentIncomingNotifType,
      );

    if (isReceiver && !isMuted && incomingMessageDataNotifSetting) {
      const isDesktopNotificationTurnOn =
        incomingMessageDataNotifSetting.settings.find(
          (setting: NotificationSettingKeyPlatform) =>
            setting.key === NOTIF_SETTING_KEY_DN,
        ).value;

      if (isDesktopNotificationTurnOn) {
        const groupChatName = newRoom?.info?.name || 'Group Chat';
        const usernameAtGroup = senderUserName ? `${senderUserName} @` : '';
        const notifTitle =
          newRoom.type === RoomTypeFromPayload.PERSONAL
            ? senderUserName
            : `${usernameAtGroup} ${groupChatName}`;
        const lastMessageType =
          lastMessage?.type || ATTACHMENT_TYPE.ATTACHMENT_TYPE_UNSPECIFIED;

        showChatNotification({
          title: notifTitle,
          description: transformToNotifMessage(
            lastMessage.text,
            lastMessageType,
          ),
          isDeactivate: newRoom?.attributes?.personal?.is_deactivated || false,
          name: newRoom?.info?.name || '',
          shortName: newRoom?.info?.shortened_name || '',
          type: newRoom?.type || RoomTypeFromPayload.PERSONAL,
          avatar: newRoom?.info?.avatar_url || '',
          id: newRoom?.id || 0,
          key: `in-app-notification-${newRoom?.id}`,
          isPersonalChat,
          isInvitedChat,
          onClose: () => {
            dispatch(webSocketSlice.actions.clearChat());
          },
        });
      }
    }
  };

  const actionEventType = {
    [EVENT_TYPE_BROADCAST.TYPE_CHAT_MESSAGE]: (incomingChat) =>
      actionChatRoom(incomingChat),
    [EVENT_TYPE_BROADCAST.TYPE_CHAT_ROOM_INFO]: (incomingChat) =>
      actionRoomList(incomingChat),
  };

  const actionTypeBroadCast = (incomingChat: ChatWSSocialV2Payload) => {
    const incomingBodyEventType = incomingChat?.body?.event?.type;
    return (
      actionEventType[incomingBodyEventType] &&
      actionEventType[incomingBodyEventType](incomingChat)
    );
  };

  const chatListener = (incomingChat: ChatWSSocialV2Payload) => {
    if (incomingChat) {
      /* Additional information

        1. SEND MESSAGE
        When user send message to group room or personal room,
        ws will send event new messages as a sender so after
        that, we will update the slice conversations.

        2. OLD WEBSOCKET
        There is 3 version of WS, the old (M) that have been
        deprecated, the new (M2) and the latest one (M3).
        In this listener we will only use M3 as our WS event.
        To prevent doubled event sented to us, we use message.id
        as identifier to check if the message has been save in
        redux/slice or not.

        3. BODY TYPE OF EVENT
        Personal room and group room message now have same
        body payload. We dont need to transform the body.

       */
      actionTypeBroadCast(incomingChat);
    }
  };

  useEffect(() => {
    if (!isUserLogin) {
      return;
    }

    // After check the behaviour, it more safe to use
    // synchronously process instead of asynchronous
    // when receive double events from ws
    // @ts-ignore
    chatListener(newIncomingChatWsSocialV2);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newIncomingChatWsSocialV2, isUserLogin]);

  return null;
}

export default useChatListenerWsSocialV2;
